/**
 * M365 Email Service
 * Frontend service for M365 email authentication and sending
 * Communicates with m365-email-auth Netlify function
 */

import { getNetlifyFunctionUrl } from '../config/netlify';

const FUNCTION_URL = getNetlifyFunctionUrl('m365-email-auth');

/**
 * Check if user is authenticated with M365 email
 * @returns {Promise<boolean>}
 */
export const isAuthenticated = async () => {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'checkAuth',
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to check authentication:', data.error);
      return false;
    }

    return data.authenticated || false;
  } catch (error) {
    console.error('Error checking authentication:', error);
    return false;
  }
};

/**
 * Initiate OAuth authentication flow
 * Redirects user to Microsoft login page
 * @returns {Promise<void>}
 */
export const authenticate = async () => {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'authenticate',
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to generate authentication URL');
    }

    if (data.authUrl) {
      window.location.href = data.authUrl;
    } else {
      throw new Error('No authentication URL returned');
    }
  } catch (error) {
    console.error('Error initiating authentication:', error);
    throw error;
  }
};

/**
 * Handle OAuth callback
 * Called after user returns from Microsoft login
 * @param {string} code - Authorization code from OAuth callback
 * @returns {Promise<object>} Account information
 */
export const handleCallback = async (code) => {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'callback',
        code: code,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to complete authentication');
    }

    return data;
  } catch (error) {
    console.error('Error handling callback:', error);
    throw error;
  }
};

/**
 * Get connected account information
 * @returns {Promise<object|null>} Account info or null if not connected
 */
export const getAccountInfo = async () => {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'getAccountInfo',
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('Failed to get account info:', data.error);
      return null;
    }

    return data.connected ? data : null;
  } catch (error) {
    console.error('Error getting account info:', error);
    return null;
  }
};

/**
 * Disconnect M365 email account
 * Removes tokens from database
 * @returns {Promise<boolean>} Success status
 */
export const disconnect = async () => {
  try {
    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'disconnect',
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to disconnect account');
    }

    return data.success;
  } catch (error) {
    console.error('Error disconnecting account:', error);
    throw error;
  }
};

/**
 * Send email via M365
 * @param {object} emailData - Email data
 * @param {string|string[]} emailData.to - Recipient email(s)
 * @param {string|string[]} [emailData.cc] - CC recipient(s)
 * @param {string|string[]} [emailData.bcc] - BCC recipient(s)
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.body - Email body (HTML)
 * @param {number} [emailData.preparativoId] - Preparativo ID for logging
 * @param {string} [emailData.stepType] - Step type for logging
 * @returns {Promise<object>} Send result
 */
export const sendEmail = async (emailData) => {
  try {
    const { to, cc, bcc, subject, body, documentFileId, preparativoId, stepType } = emailData;

    if (!to || !subject || !body) {
      throw new Error('Missing required fields: to, subject, body');
    }

    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'sendEmail',
        to,
        cc,
        bcc,
        subject,
        body,
        documentFileId,
        preparativoId,
        stepType,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email');
    }

    return data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

/**
 * Send email with attachments via M365
 * @param {object} emailData - Email data
 * @param {string|string[]} emailData.to - Recipient email(s)
 * @param {string|string[]} [emailData.cc] - CC recipient(s)
 * @param {string|string[]} [emailData.bcc] - BCC recipient(s)
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.body - Email body (HTML)
 * @param {Array<object>} emailData.attachments - Array of attachments
 * @param {string} emailData.attachments[].name - File name
 * @param {string} emailData.attachments[].contentType - MIME type
 * @param {string} emailData.attachments[].contentBytes - Base64 encoded file content
 * @param {number} [emailData.preparativoId] - Preparativo ID for logging
 * @param {string} [emailData.stepType] - Step type for logging
 * @returns {Promise<object>} Send result
 */
export const sendEmailWithAttachments = async (emailData) => {
  try {
    const { to, cc, bcc, subject, body, attachments, preparativoId, stepType } = emailData;

    if (!to || !subject || !body) {
      throw new Error('Missing required fields: to, subject, body');
    }

    if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
      throw new Error('Attachments are required for this function');
    }

    // Validate attachment sizes (4MB limit per attachment)
    // Approximate size from base64 string length (base64 is ~33% larger than binary)
    const maxSize = 4 * 1024 * 1024; // 4MB in bytes
    for (const att of attachments) {
      // Calculate approximate binary size from base64 string
      const base64Length = att.contentBytes.length;
      const padding = (att.contentBytes.match(/=/g) || []).length;
      const sizeBytes = (base64Length * 3 / 4) - padding;
      
      if (sizeBytes > maxSize) {
        throw new Error(`Attachment ${att.name} exceeds 4MB size limit`);
      }
    }

    const response = await fetch(FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        action: 'sendEmail',
        to,
        cc,
        bcc,
        subject,
        body,
        attachments,
        preparativoId,
        stepType,
      }),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to send email with attachments');
    }

    return data;
  } catch (error) {
    console.error('Error sending email with attachments:', error);
    throw error;
  }
};

/**
 * Convert File object to base64 for attachment
 * @param {File} file - File object
 * @returns {Promise<object>} Attachment object with name, contentType, and contentBytes
 */
export const fileToAttachment = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data URL prefix
      resolve({
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        contentBytes: base64,
      });
    };
    
    reader.onerror = () => {
      reject(new Error(`Failed to read file: ${file.name}`));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Convert multiple files to attachments
 * @param {File[]} files - Array of File objects
 * @returns {Promise<Array<object>>} Array of attachment objects
 */
export const filesToAttachments = async (files) => {
  try {
    const attachmentPromises = files.map(file => fileToAttachment(file));
    return await Promise.all(attachmentPromises);
  } catch (error) {
    console.error('Error converting files to attachments:', error);
    throw error;
  }
};

export default {
  isAuthenticated,
  authenticate,
  handleCallback,
  getAccountInfo,
  disconnect,
  sendEmail,
  sendEmailWithAttachments,
  fileToAttachment,
  filesToAttachments,
};
